import {
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { siteComplianceApi } from '@/lib/siteComplianceApi';

import type { SiteComplianceEvaluation } from '../siteComplianceEvaluation.types';
import {
  toSiteComplianceAssessmentData,
  toSiteComplianceEvaluation,
  toSiteComplianceEvaluationDetails,
  toSiteComplianceJobStatusResponse,
} from './mappers';
import type {
  AssessmentDataDto,
  CheckpointSettingDto,
  EvaluationDetailDto,
  EvaluationsPageDto,
  JobStatusResponse,
  JobStatusResponseDto,
  SdkCheckpointMetadataDto,
  SiteComplianceAssessmentData,
} from './types';

const SITE_COMPLIANCE_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  retry: 1,
  staleTime: 30_000,
};

export const SITE_COMPLIANCE_CHECKPOINTS_QUERY_KEY = [
  'siteComplianceCheckpoints',
] as const;

export interface SiteComplianceEvaluationsQueryParams {
  cursor?: string | null;
  latestOnly?: boolean;
  pageSize?: number;
  siteIds?: string[];
}

interface SiteComplianceEvaluationsPage {
  continuationToken: string | null;
  items: SiteComplianceEvaluation[];
}

interface SiteComplianceJobStatusQueryOptions {
  enabled?: boolean;
  onSettled?: (
    data: JobStatusResponse | null,
    error: Error | null,
  ) => void;
}

interface SiteComplianceAssessmentQueryOptions {
  enabled?: boolean;
}

interface SiteComplianceQueryOptions {
  enabled?: boolean;
}

const deduplicateEvaluations = (
  evaluations: SiteComplianceEvaluation[],
): SiteComplianceEvaluation[] => {
  const seen = new Set<string>();

  return evaluations.filter((evaluation) => {
    if (seen.has(evaluation.id)) {
      return false;
    }

    seen.add(evaluation.id);
    return true;
  });
};

export const fetchSiteComplianceCheckpoints = async () => {
  const response = await siteComplianceApi.get<CheckpointSettingDto[]>(
    '/api/v1/system/checkpoints',
  );

  return response.data ?? [];
};

export const fetchSdkCheckpointsCatalog = async () => {
  const response = await siteComplianceApi.get<SdkCheckpointMetadataDto[]>(
    '/api/v1/site-compliance/jobs/checkpoints',
  );

  return response.data ?? [];
};

const fetchSiteComplianceEvaluationsPage = async (
  params: SiteComplianceEvaluationsQueryParams,
): Promise<SiteComplianceEvaluationsPage> => {
  const response = await siteComplianceApi.get<EvaluationsPageDto>(
    '/api/v1/site-compliance/evaluations',
    {
      params: {
        continuationToken: params.cursor || undefined,
        latestOnly: params.latestOnly,
        pageSize: params.pageSize,
        siteIds: params.siteIds?.length ? params.siteIds : undefined,
      },
    },
  );

  return {
    continuationToken: response.data?.continuationToken ?? null,
    items: (response.data?.items ?? []).map(toSiteComplianceEvaluation),
  };
};

export const fetchSiteComplianceEvaluationDetails = async (
  evaluationId: string,
) => {
  const response = await siteComplianceApi.get<EvaluationDetailDto>(
    `/api/v1/site-compliance/evaluations/${encodeURIComponent(evaluationId)}`,
    {
      params: evaluationId.includes('!')
        ? undefined
        : {
            siteId: evaluationId,
          },
    },
  );

  return toSiteComplianceEvaluationDetails(response.data);
};

export const fetchSiteComplianceJobStatus = async (
  jobId: string,
): Promise<JobStatusResponse> => {
  const response = await siteComplianceApi.get<JobStatusResponseDto>(
    `/api/v1/site-compliance/jobs/${encodeURIComponent(jobId)}/status`,
  );

  return toSiteComplianceJobStatusResponse(response.data, jobId);
};

export const fetchSiteComplianceAssessment = async (
  evaluationId: string,
  checkpointId: string,
): Promise<SiteComplianceAssessmentData> => {
  const response = await siteComplianceApi.get<AssessmentDataDto>(
    `/api/v1/site-compliance/evaluations/${encodeURIComponent(
      evaluationId,
    )}/assessments/${encodeURIComponent(checkpointId)}`,
  );

  return toSiteComplianceAssessmentData(
    response.data,
    evaluationId,
    checkpointId,
  );
};

export const useSiteComplianceCheckpointsQuery = () =>
  useQuery({
    queryFn: fetchSiteComplianceCheckpoints,
    queryKey: SITE_COMPLIANCE_CHECKPOINTS_QUERY_KEY,
    ...SITE_COMPLIANCE_QUERY_OPTIONS,
  });

export const useSdkCheckpointsCatalogQuery = (
  options: SiteComplianceQueryOptions = {},
) =>
  useQuery({
    enabled: options.enabled ?? true,
    queryFn: fetchSdkCheckpointsCatalog,
    queryKey: ['site-compliance', 'sdk-checkpoints-catalog'],
    ...SITE_COMPLIANCE_QUERY_OPTIONS,
  });

export const useSiteComplianceEvaluationsQuery = (
  params: SiteComplianceEvaluationsQueryParams = {},
) => {
  const query = useInfiniteQuery({
    getNextPageParam: (lastPage: SiteComplianceEvaluationsPage) =>
      lastPage.continuationToken || undefined,
    initialPageParam: params.cursor ?? null,
    queryFn: ({ pageParam }) =>
      fetchSiteComplianceEvaluationsPage({
        ...params,
        cursor: pageParam,
      }),
    queryKey: ['site-compliance', 'evaluations', params],
    ...SITE_COMPLIANCE_QUERY_OPTIONS,
  });
  const evaluations = deduplicateEvaluations(
    query.data?.pages.flatMap((page) => page.items) ?? [],
  );

  return {
    ...query,
    evaluations,
    getEvaluations: () => evaluations,
  };
};

export const useSiteComplianceEvaluationQuery = (evaluationId: string) =>
  useQuery({
    enabled: Boolean(evaluationId),
    queryFn: () => fetchSiteComplianceEvaluationDetails(evaluationId),
    queryKey: ['site-compliance', 'evaluation', evaluationId],
    ...SITE_COMPLIANCE_QUERY_OPTIONS,
  });

export const isSiteComplianceJobTerminalStatus = (
  status: JobStatusResponse['status'] | null | undefined,
) => status === 'Completed' || status === 'Failed' || status === 'TimedOut';

export const getSiteComplianceJobPollingDelayMs = (attempt: number) =>
  Math.min(1000 * 2 ** attempt, 15_000);

export const useSiteComplianceJobStatusQuery = (
  jobId: string | null | undefined,
  options: SiteComplianceJobStatusQueryOptions = {},
) => {
  const attemptRef = useRef(0);
  const settledJobRef = useRef<string | null>(null);
  const enabled = Boolean(jobId) && (options.enabled ?? true);
  const query = useQuery({
    enabled,
    queryFn: () => fetchSiteComplianceJobStatus(jobId ?? ''),
    queryKey: ['site-compliance', 'job-status', jobId],
    refetchInterval: (queryState) => {
      const status = queryState.state.data?.status;

      if (isSiteComplianceJobTerminalStatus(status)) {
        return false;
      }

      const delay = getSiteComplianceJobPollingDelayMs(attemptRef.current);
      attemptRef.current += 1;
      return delay;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    attemptRef.current = 0;
    settledJobRef.current = null;
  }, [jobId]);

  useEffect(() => {
    if (!jobId || settledJobRef.current === jobId) {
      return;
    }

    if (query.error instanceof Error) {
      settledJobRef.current = jobId;
      options.onSettled?.(null, query.error);
      return;
    }

    if (query.data && isSiteComplianceJobTerminalStatus(query.data.status)) {
      settledJobRef.current = jobId;
      options.onSettled?.(query.data, null);
    }
  }, [jobId, options, query.data, query.error]);

  return query;
};

export const useSiteComplianceAssessmentQuery = (
  evaluationId: string | null | undefined,
  checkpointId: string | null | undefined,
  options: SiteComplianceAssessmentQueryOptions = {},
) =>
  useQuery({
    enabled:
      Boolean(evaluationId) &&
      Boolean(checkpointId) &&
      (options.enabled ?? true),
    queryFn: () =>
      fetchSiteComplianceAssessment(evaluationId ?? '', checkpointId ?? ''),
    queryKey: [
      'site-compliance',
      'assessment',
      evaluationId,
      checkpointId,
    ],
    ...SITE_COMPLIANCE_QUERY_OPTIONS,
  });
